import { pence, taxYear } from '@cf/shared';
/**
 * UK tax year 2025/26 rule set — rest-of-UK (England, Wales, Northern
 * Ireland) only; Scottish income tax is a hard-gated product boundary (see
 * region.ts). Every figure below is cited in `sources` and cross-checked in
 * docs/tax/hmrc-validation.md.
 */
export const rules2025_26 = {
    taxYear: taxYear(2025),
    version: '2025-26.5',
    incomeTax: {
        personalAllowance: pence(12_570_00),
        personalAllowanceTaperThreshold: pence(100_000_00),
        basicBandTop: pence(37_700_00),
        additionalRateThreshold: pence(125_140_00),
        basicRateBps: 2000,
        higherRateBps: 4000,
        additionalRateBps: 4500,
        savingsStartingRateBand: pence(5_000_00),
        personalSavingsAllowanceBasic: pence(1_000_00),
        personalSavingsAllowanceHigher: pence(500_00),
        personalSavingsAllowanceAdditional: pence(0),
        dividendAllowance: pence(500_00),
        dividendBasicRateBps: 875,
        dividendHigherRateBps: 3375,
        dividendAdditionalRateBps: 3935,
        redundancyExemption: pence(30_000_00),
    },
    nationalInsurance: {
        class1PrimaryThreshold: pence(12_570_00),
        class1UpperEarningsLimit: pence(50_270_00),
        class1MainRateBps: 800,
        class1UpperRateBps: 200,
        class4LowerProfitsLimit: pence(12_570_00),
        class4UpperProfitsLimit: pence(50_270_00),
        class4MainRateBps: 600,
        class4UpperRateBps: 200,
    },
    capitalGains: {
        annualExemptAmount: pence(3_000_00),
        lowerRateBps: 1800,
        higherRateBps: 2400,
        // From 30 Oct 2024 residential rates are aligned with the main rates.
        residentialLowerRateBps: 1800,
        residentialHigherRateBps: 2400,
        badrRateBps: 1400,
        badrLifetimeLimit: pence(1_000_000_00),
    },
    pension: {
        annualAllowance: pence(60_000_00),
        moneyPurchaseAnnualAllowance: pence(10_000_00),
        taperThresholdIncome: pence(200_000_00),
        taperAdjustedIncome: pence(260_000_00),
        taperMinimumAllowance: pence(10_000_00),
        taxFreeLumpSumFractionBps: 2500,
        lumpSumAllowance: pence(268_275_00),
        lumpSumAndDeathBenefitAllowance: pence(1_073_100_00),
        nonEarnerAnnualAllowanceFloor: pence(3_600_00),
        // NMPA is 55 for this tax year (legislated rise to 57 on 6 Apr 2028).
        normalMinimumPensionAge: 55,
    },
    isa: {
        annualSubscriptionLimit: pence(20_000_00),
        lisaAnnualLimit: pence(4_000_00),
        lisaBonusBps: 2500,
        lisaMaxContributionAge: 50,
        lisaPenaltyFreeAge: 60,
        jisaAnnualLimit: pence(9_000_00),
    },
    nsi: {
        premiumBondMaxHolding: pence(50_000_00),
    },
    inheritanceTax: {
        nilRateBand: pence(325_000_00),
        residenceNilRateBand: pence(175_000_00),
        residenceNilRateBandTaperThreshold: pence(2_000_000_00),
        standardRateBps: 4000,
        charityRateBps: 3600,
        annualGiftExemption: pence(3_000_00),
        // A2.1. Gifts on the occasion of a marriage or civil partnership, by the
        // giver's relationship to the couple. £2,500 also covers one of the couple
        // giving to the other; the tool models the giver as `grandparent` there
        // only if an adviser records it that way, which is why the register names
        // the three amounts rather than the four cases.
        weddingGiftExemption: {
            parent: pence(5_000_00),
            grandparent: pence(2_500_00),
            other: pence(1_000_00),
        },
        // Unused pension funds enter the chargeable estate from 6 April 2027
        // (announced Autumn Budget 2024, confirmed 2025). Carried on THIS rule set
        // because a projection started in 2025/26 runs decades past the change and
        // must switch treatment at the right year rather than applying pre-2027
        // treatment for its whole length.
        unusedPensionFundsInEstateFrom: '2027-04-06',
        // Taper relief on the IHT due on a failed PET, by years survived after the gift.
        giftTaperBands: [
            { fromYears: 0, toYears: 3, rateBps: 10_000 },
            { fromYears: 3, toYears: 4, rateBps: 8000 },
            { fromYears: 4, toYears: 5, rateBps: 6000 },
            { fromYears: 5, toYears: 6, rateBps: 4000 },
            { fromYears: 6, toYears: 7, rateBps: 2000 },
        ],
    },
    section24: {
        financeCostCreditRateBps: 2000,
    },
    sdlt: {
        bands: [
            { upTo: pence(125_000_00), rateBps: 0 },
            { upTo: pence(250_000_00), rateBps: 200 },
            { upTo: pence(925_000_00), rateBps: 500 },
            { upTo: pence(1_500_000_00), rateBps: 1000 },
            { upTo: null, rateBps: 1200 },
        ],
        ftbNilRateBand: pence(300_000_00),
        ftbRateBps: 500,
        ftbMaxPrice: pence(500_000_00),
        additionalDwellingSurchargeBps: 500,
    },
    sources: [
        {
            ruleKey: 'incomeTax.personalAllowance+bands+taper',
            title: 'HMRC — Income Tax rates and allowances, current and past (2025 to 2026)',
            url: 'https://www.gov.uk/government/publications/rates-and-allowances-income-tax/income-tax-rates-and-allowances-current-and-past',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'incomeTax.dividendAllowance+dividendRates',
            title: 'HMRC — Income Tax rates and allowances, current and past (dividends 2025 to 2026)',
            url: 'https://www.gov.uk/government/publications/rates-and-allowances-income-tax/income-tax-rates-and-allowances-current-and-past',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'incomeTax.savingsStartingRateBand+personalSavingsAllowance',
            title: 'GOV.UK — Tax on savings interest (starting rate £5,000; PSA £1,000/£500/£0)',
            url: 'https://www.gov.uk/apply-tax-free-interest-on-savings',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'nationalInsurance.class1',
            title: 'HMRC — Rates and thresholds for employers 2025 to 2026 (PT £12,570; UEL £50,270; 8%/2%)',
            url: 'https://www.gov.uk/guidance/rates-and-thresholds-for-employers-2025-to-2026',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'nationalInsurance.class4',
            title: 'GOV.UK — Self-employed National Insurance rates (LPL £12,570; UPL £50,270; 6%/2%)',
            url: 'https://www.gov.uk/self-employed-national-insurance-rates',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'capitalGains.annualExemptAmount+rates',
            title: 'HMRC — Capital Gains Tax rates and annual tax-free allowances (2025 to 2026: AEA £3,000; 18%/24%)',
            url: 'https://www.gov.uk/government/publications/rates-and-allowances-capital-gains-tax/capital-gains-tax-rates-and-annual-tax-free-allowances',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'pension.annualAllowance+taper+mpaa',
            title: 'GOV.UK — Tax on your private pension contributions: annual allowance (AA £60,000; threshold £200,000; adjusted £260,000)',
            url: 'https://www.gov.uk/tax-on-your-private-pension/annual-allowance',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'pension.taperMinimumAllowance+mpaaAmount',
            title: 'HMRC — Work out your reduced (tapered) annual allowance (minimum £10,000; MPAA £10,000)',
            url: 'https://www.gov.uk/guidance/pension-schemes-work-out-your-tapered-annual-allowance',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'pension.annualAllowance.carryForward',
            title: 'HMRC Pensions Tax Manual PTM055100 — Annual allowance: carry forward (unused allowance from the previous three tax years, current year used first, then oldest)',
            url: 'https://www.gov.uk/hmrc-internal-manuals/pensions-tax-manual/ptm055100',
            retrievedOn: '2026-07-31',
        },
        {
            ruleKey: 'pension.lumpSumAllowance+lsdba',
            title: 'GOV.UK — Tax when you get a pension: lump sum allowance (LSA £268,275; LSDBA £1,073,100)',
            url: 'https://www.gov.uk/tax-on-your-private-pension/lump-sum-allowance',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'isa.annualSubscriptionLimit',
            title: 'GOV.UK — Individual Savings Accounts: £20,000 shared across all ISA types per tax year',
            url: 'https://www.gov.uk/individual-savings-accounts',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'isa.lisaAnnualLimit+lisaBonus+lisaAges',
            title: 'GOV.UK — Lifetime ISA: pay in up to £4,000 a year (within the £20,000 ISA allowance) until age 50; 25% government bonus; withdraw without charge from age 60',
            url: 'https://www.gov.uk/lifetime-isa',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'capitalGains.badrRateBps+badrLifetimeLimit',
            title: 'GOV.UK — Business Asset Disposal Relief: qualifying gains taxed at 14% for 2025/26 and 18% from 6 April 2026, £1 million lifetime limit',
            url: 'https://www.gov.uk/business-asset-disposal-relief',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'incomeTax.redundancyExemption',
            title: 'GOV.UK — Redundancy pay: up to £30,000 of a termination payment is tax-free (s.401 ITEPA 2003)',
            url: 'https://www.gov.uk/redundancy-your-rights/redundancy-pay',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'pension.normalMinimumPensionAge',
            title: 'GOV.UK — Increasing Normal Minimum Pension Age: NMPA is 55, rising to 57 on 6 April 2028 (protected pension ages and ill-health access excepted)',
            url: 'https://www.gov.uk/government/publications/increasing-normal-minimum-pension-age',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'nsi.premiumBondMaxHolding',
            title: 'NS&I — Premium Bonds: maximum holding £50,000 per person (product limit)',
            url: 'https://www.nsandi.com/products/premium-bonds',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'isa.jisaAnnualLimit',
            title: 'GOV.UK — Junior Individual Savings Accounts: £9,000 per child per tax year',
            url: 'https://www.gov.uk/junior-individual-savings-accounts',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'inheritanceTax.nilRateBand+rates+spouseExemption',
            title: 'GOV.UK — Inheritance Tax (NRB £325,000; 40%; 36% charity rate)',
            url: 'https://www.gov.uk/inheritance-tax',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'inheritanceTax.residenceNilRateBand+taper',
            title: 'HMRC — Residence nil rate band (RNRB £175,000; taper above £2m)',
            url: 'https://www.gov.uk/guidance/inheritance-tax-residence-nil-rate-band',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'inheritanceTax.weddingGiftExemption',
            title: 'GOV.UK — How Inheritance Tax on a gift is worked out: wedding/civil-partnership gifts exempt up to £5,000 (parent), £2,500 (grandparent or remoter ancestor), £1,000 (anyone else); separate from and additional to the annual exemption',
            url: 'https://www.gov.uk/inheritance-tax/gifts',
            retrievedOn: '2026-09-06',
        },
        {
            ruleKey: 'inheritanceTax.annualGiftExemption+taperRelief',
            title: 'GOV.UK — How Inheritance Tax on a gift is worked out: annual exemption £3,000; taper relief 100/80/60/40/20% over 3–7 years',
            url: 'https://www.gov.uk/inheritance-tax/gifts',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'capitalGains.residentialRates',
            title: 'HMRC — Capital Gains Tax rates: residential property gains aligned with main rates (18%/24%) from 30 October 2024',
            url: 'https://www.gov.uk/government/publications/rates-and-allowances-capital-gains-tax/capital-gains-tax-rates-and-annual-tax-free-allowances',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'sdlt.bands+ftbRelief+additionalDwellingSurcharge',
            title: 'GOV.UK — Stamp Duty Land Tax: residential rates (0/2/5/10/12%; FTB nil to £300k, 5% to £500k; +5% additional dwellings)',
            url: 'https://www.gov.uk/stamp-duty-land-tax/residential-property-rates',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'section24.financeCostCreditRateBps',
            title: 'HMRC — Tax relief for residential landlords: how it is worked out (100% at basic rate since 2020/21; capped)',
            url: 'https://www.gov.uk/guidance/changes-to-tax-relief-for-residential-landlords-how-its-worked-out-including-case-studies',
            retrievedOn: '2026-07-08',
        },
    ],
    review: {
        // Automated cross-check against the HMRC sources above, evidenced in
        // docs/tax/hmrc-validation.md, then countersigned by a named human tax
        // reviewer who re-verified every figure against the live HMRC references.
        //
        // NOTE (2025-26.2): the Stage 3 additions — residential-CGT rate fields
        // (aligned with the main 18%/24% rates), the £3,000 annual gift exemption,
        // the IHT taper-relief bands, and the £3,600 non-earner pension floor — are
        // long-stable, cited HMRC constants added AFTER the original countersign.
        // `reviewedVersion` records what the human actually signed (2025-26.1), so
        // the gate reports the delta as PENDING re-countersign rather than letting
        // the old signature silently cover the new figures (QUESTIONS.md #3).
        //
        // NOTE (2025-26.3): Stages 4–5 add the Lifetime ISA figures (£4,000 limit
        // inside the shared allowance, 25% bonus, ages 50/60), the £9,000 Junior
        // ISA limit, the normal minimum pension age (55), the NS&I premium-bond
        // £50,000 product cap, BADR (14%, £1m lifetime limit) and the £30,000
        // redundancy exemption — all long-stable, cited constants pending the
        // same re-countersign.
        //
        // NOTE (2025-26.4): adds `unusedPensionFundsInEstateFrom` (6 April 2027),
        // the date from which unused pension funds form part of the chargeable
        // estate. This is a POLICY EFFECTIVE DATE, not a rate or threshold, and it
        // changes the IHT figure for any plan modelling a death on or after it —
        // so it needs the same human re-countersign as every other post-signature
        // addition above, and `reviewedVersion` continues to record what was
        // actually signed. See docs/tax/pensions-in-estate-2027.md.
        // COUNTERSIGNED 2026-07-31, covering 2025-26.4: the .2/.3/.4 deltas listed
        // above AND the round-4 annual-allowance carry-forward mechanism +
        // PTM055100 citation (docs/tax/pension-caps-gap-audit-2026-07-31.md). That
        // signature carried a TEMP DEV ONLY attribution — a development-phase
        // stand-in, which the release gate now refuses.
        //
        // RE-COUNTERSIGNED 2026-09-11 by Caleb Saunderson as the named tax
        // reviewer, extending the signature to the current version (2025-26.5) and
        // binding it to the content fingerprint recorded below. It covers the .5
        // delta — the wedding-gift exemption and the annual gift exemption as a
        // per-tax-year allowance with one year's carry-forward — re-verified
        // against the HMRC references in docs/tax/iht-gifts-exemptions.md and
        // recorded in docs/tax/hmrc-validation.md. This is the signature both the
        // CI gate (scripts/check-tax-review-gate.mjs) and the runtime gate
        // (`releaseRefusal`) read before a figure here reaches a client.
        reviewedBy: 'Caleb Saunderson',
        reviewedAt: '2026-09-11',
        // 2025-26.5 (A2.1, 2026-09-06): `weddingGiftExemption` added (£5,000 /
        // £2,500 / £1,000) and the annual gift exemption changed from one lifetime
        // pool to per tax year with one year's carry-forward. `reviewedVersion`
        // moves to .5 because the 2026-09-11 countersign above names these figures
        // explicitly — it does not silently inherit them (hard rule #11).
        // `reviewedFingerprint` binds the signature to the CONTENT, not the label:
        // move any rate, band or threshold without a fresh countersign and
        // `releaseRefusal` reports the set as stale rather than letting this
        // signature stretch to cover figures nobody read.
        reviewedVersion: '2025-26.5',
        reviewedFingerprint: 'c4f0d7f7350440e4',
    },
};
/**
 * Version tag stamped onto every projection_run (`tax_rules_version`) for
 * reproducibility. Bump whenever any figure or calculator behaviour for this
 * tax year changes.
 */
export const TAX_RULES_VERSION = rules2025_26.version;
//# sourceMappingURL=2025-26.js.map